import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LocalDate } from "@/components/ui/local-date";
import { ApiError, api } from "@/lib/api";
import { formatNumber } from "@/lib/i18n";
import { useApiMutation } from "@/lib/use-api-mutation";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * `/accounting/cash` — cash registers and the cash book.
 *
 * The balance is never edited: it is entered once when the register is
 * created and from then on the documents decide it. Money that appeared
 * without a document is exactly what a cash book prevents.
 */

type Register = {
  registerId: string;
  name: string;
  currency: string;
  openingBalance: string;
  openingOn: string;
  income: string;
  expense: string;
  balance: string;
  documentCount: number;
};

type BookRow = {
  id: string;
  number: string;
  issuedOn: string;
  description: string;
  income: string | null;
  expense: string | null;
  balance: string;
};

const INPUT = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm";

export const Route = createFileRoute("/_app/accounting/cash")({
  loader: async () => {
    try {
      return await api.get<{ registers: Register[] }>("/api/accounting/cash");
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: CashPage,
});

function money(amount: string, currency: string): string {
  return `${formatNumber(Number(amount), undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function CashPage() {
  const { t } = useTranslation();
  const { registers } = Route.useLoaderData();
  const router = useRouter();
  const [openBook, setOpenBook] = useState<string | null>(null);

  const addRegister = useApiMutation(
    (body: Record<string, unknown>) => api.post("/api/accounting/cash", body),
    { onSuccess: () => router.invalidate(), errorMessage: () => t("accCash.createRegisterFailed") },
  );

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="font-semibold text-2xl">{t("accCash.title")}</h1>
        <p className="text-muted-foreground text-sm">{t("accCash.subtitle")}</p>
      </div>

      {registers.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-muted-foreground text-sm">
            {t("accCash.noRegister")}
          </CardContent>
        </Card>
      ) : (
        registers.map((register) => (
          <Card key={register.registerId}>
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle className="text-base">{register.name}</CardTitle>
                <CardDescription>
                  {t("accCash.openingLine", {
                    amount: money(register.openingBalance, register.currency),
                  })}
                  <LocalDate value={register.openingOn} /> ·{" "}
                  {t("accCash.documents", { count: register.documentCount })}
                </CardDescription>
              </div>
              <div className="text-right">
                <div className="font-semibold text-xl">
                  {money(register.balance, register.currency)}
                </div>
                <div className="text-muted-foreground text-xs">
                  {t("accCash.inOut", {
                    income: money(register.income, register.currency),
                    expense: money(register.expense, register.currency),
                  })}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <NewDocumentForm register={register} onDone={() => router.invalidate()} />
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setOpenBook(openBook === register.registerId ? null : register.registerId)
                }
              >
                {openBook === register.registerId ? t("accCash.hideBook") : t("accCash.cashBook")}
              </Button>
              {openBook === register.registerId && <CashBook register={register} />}
            </CardContent>
          </Card>
        ))
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t("accCash.newRegister")}</CardTitle>
          <CardDescription>{t("accCash.newRegisterHint")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-3 sm:grid-cols-5"
            onSubmit={(event) => {
              event.preventDefault();
              const fd = new FormData(event.currentTarget);
              addRegister.mutate({
                name: String(fd.get("name") ?? "").trim(),
                currency: String(fd.get("currency") ?? "CZK").trim() || "CZK",
                openingBalance: Number(fd.get("openingBalance") ?? 0),
                openingOn: String(fd.get("openingOn") ?? ""),
              });
              event.currentTarget.reset();
            }}
          >
            <div className="sm:col-span-2">
              <label htmlFor="name" className="text-muted-foreground text-xs">
                {t("accCash.name")}
              </label>
              <input
                id="name"
                name="name"
                required
                placeholder={t("accCash.namePlaceholder")}
                className={INPUT}
              />
            </div>
            <div>
              <label htmlFor="currency" className="text-muted-foreground text-xs">
                {t("accCash.currency")}
              </label>
              <input id="currency" name="currency" defaultValue="CZK" className={INPUT} />
            </div>
            <div>
              <label htmlFor="openingBalance" className="text-muted-foreground text-xs">
                {t("accCash.openingBalance")}
              </label>
              <input
                id="openingBalance"
                name="openingBalance"
                type="number"
                step="0.01"
                defaultValue="0"
                className={INPUT}
              />
            </div>
            <div>
              <label htmlFor="openingOn" className="text-muted-foreground text-xs">
                {t("accCash.asOfDate")}
              </label>
              <input id="openingOn" name="openingOn" type="date" required className={INPUT} />
            </div>
            <div className="sm:col-span-5">
              <Button type="submit" disabled={addRegister.pending}>
                {addRegister.pending ? t("accCash.creating") : t("accCash.createRegister")}
              </Button>
              {addRegister.error && (
                <span className="ml-2 text-destructive text-sm">{addRegister.error}</span>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function NewDocumentForm({ register, onDone }: { register: Register; onDone: () => void }) {
  const { t } = useTranslation();
  const issue = useApiMutation(
    (body: Record<string, unknown>) => api.post("/api/accounting/cash/documents", body),
    { onSuccess: onDone, errorMessage: () => t("accCash.issueFailed") },
  );

  return (
    <form
      className="grid gap-3 border-t pt-4 sm:grid-cols-6"
      onSubmit={(event) => {
        event.preventDefault();
        const fd = new FormData(event.currentTarget);
        issue.mutate({
          registerId: register.registerId,
          type: String(fd.get("type")),
          issuedOn: String(fd.get("issuedOn") ?? ""),
          amount: Number(fd.get("amount") ?? 0),
          description: String(fd.get("description") ?? "").trim(),
          partnerName: String(fd.get("partnerName") ?? "").trim() || null,
        });
        event.currentTarget.reset();
      }}
    >
      <div>
        <label htmlFor={`type-${register.registerId}`} className="text-muted-foreground text-xs">
          {t("accCash.type")}
        </label>
        <select id={`type-${register.registerId}`} name="type" className={INPUT}>
          <option value="income">{t("accCash.receipt")}</option>
          <option value="expense">{t("accCash.payment")}</option>
        </select>
      </div>
      <div>
        <label htmlFor={`date-${register.registerId}`} className="text-muted-foreground text-xs">
          {t("accCash.date")}
        </label>
        <input
          id={`date-${register.registerId}`}
          name="issuedOn"
          type="date"
          required
          className={INPUT}
        />
      </div>
      <div>
        <label htmlFor={`amount-${register.registerId}`} className="text-muted-foreground text-xs">
          {t("accCash.amount")}
        </label>
        <input
          id={`amount-${register.registerId}`}
          name="amount"
          type="number"
          step="0.01"
          min="0.01"
          required
          className={INPUT}
        />
      </div>
      <div className="sm:col-span-2">
        <label htmlFor={`desc-${register.registerId}`} className="text-muted-foreground text-xs">
          {t("accCash.purpose")}
        </label>
        <input
          id={`desc-${register.registerId}`}
          name="description"
          required
          placeholder={t("accCash.purposePlaceholder")}
          className={INPUT}
        />
      </div>
      <div className="flex items-end">
        <Button type="submit" className="w-full" disabled={issue.pending}>
          {issue.pending ? t("accCash.issuing") : t("accCash.issue")}
        </Button>
      </div>
      {issue.error && <p className="text-destructive text-sm sm:col-span-6">{issue.error}</p>}
    </form>
  );
}

function CashBook({ register }: { register: Register }) {
  const { t } = useTranslation();
  const [year, setYear] = useState(new Date().getFullYear());
  const [book, setBook] = useState<{
    year: number;
    opening: string;
    rows: BookRow[];
    years: number[];
  } | null>(null);

  if (book === null || book.year !== year) {
    void api
      .get<{ opening: string; rows: BookRow[]; closing: string; years: number[] }>(
        `/api/accounting/cash/${register.registerId}/book?from=${year}-01-01&to=${year}-12-31`,
      )
      .then((b) => setBook({ year, opening: b.opening, rows: b.rows, years: b.years }))
      .catch(() => setBook({ year, opening: "0", rows: [], years: [year] }));
    return <p className="text-muted-foreground text-sm">{t("accCash.loading")}</p>;
  }
  const { opening, rows, years } = book;

  return (
    <div className="space-y-2">
      <div className="flex gap-3 text-sm">
        {years.map((y) => (
          <button
            key={y}
            type="button"
            onClick={() => setYear(y)}
            className={y === year ? "font-semibold" : "text-muted-foreground hover:underline"}
          >
            {y}
          </button>
        ))}
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-muted-foreground">
          <tr>
            <th className="py-1 pr-4 font-medium">{t("accCash.colDocument")}</th>
            <th className="py-1 pr-4 font-medium">{t("accCash.colDate")}</th>
            <th className="py-1 pr-4 font-medium">{t("accCash.colPurpose")}</th>
            <th className="py-1 pr-4 text-right font-medium">{t("accCash.colIn")}</th>
            <th className="py-1 pr-4 text-right font-medium">{t("accCash.colOut")}</th>
            <th className="py-1 text-right font-medium">{t("accCash.colBalance")}</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t text-muted-foreground">
            <td className="py-1 pr-4" colSpan={5}>
              {t("accCash.openingBalance")}
            </td>
            <td className="py-1 text-right tabular-nums">{money(opening, register.currency)}</td>
          </tr>
          {rows.map((row) => (
            <tr key={row.id} className="border-t">
              <td className="py-1 pr-4 font-mono text-xs">{row.number}</td>
              <td className="py-1 pr-4 text-muted-foreground">
                <LocalDate value={row.issuedOn} />
              </td>
              <td className="py-1 pr-4">{row.description}</td>
              <td className="py-1 pr-4 text-right tabular-nums text-emerald-600">
                {row.income ? money(row.income, register.currency) : ""}
              </td>
              <td className="py-1 pr-4 text-right tabular-nums">
                {row.expense ? money(row.expense, register.currency) : ""}
              </td>
              <td className="py-1 text-right tabular-nums">
                {money(row.balance, register.currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
